// Canonical date helpers for the frontend.
//
// The app's stored dates are IST calendar dates (see backend models). These
// helpers keep display and day-of-week logic INDEPENDENT of the viewer's machine
// timezone, so a mis-set laptop clock can never shift a calendar date by a day
// (the class of bug that showed "5 Jul" for a 6-Jul leave). True timestamps are
// rendered in IST via formatInstant so they're stable regardless of the clock.
const APP_TZ = 'Asia/Kolkata';

// Parse a 'YYYY-MM-DD' (or ISO 'YYYY-MM-DDThh:mm:ssZ') string into a LOCAL Date
// at midnight, built from the Y/M/D components. Because we read back the same
// components we put in, the calendar date is preserved in every timezone.
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const datePart = String(dateStr).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  if ([year, month, day].some(Number.isNaN)) return null;
  return new Date(year, month - 1, day);
};

// Display a calendar date (date-only value) — never shifts by timezone.
export const formatCalendarDate = (
  value,
  opts = { year: 'numeric', month: 'short', day: 'numeric' }
) => {
  const d = parseLocalDate(value);
  return d ? d.toLocaleDateString('en-IN', opts) : '-';
};

// Display a true instant (timestamp) in the app timezone (IST), so a machine
// whose clock is set to another zone still shows the correct India time.
export const formatInstant = (
  value,
  opts = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
) => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString('en-IN', { timeZone: APP_TZ, ...opts });
};

// Today's IST calendar date as 'YYYY-MM-DD' — use for <input type="date"> min/max
// instead of new Date().toISOString() (which is the UTC date).
export const todayISODate = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TZ, year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date());

export const isSunday = (date) => {
  const d = parseLocalDate(date);
  return d ? d.getDay() === 0 : false;
};

export const isSecondOrFourthSaturday = (date) => {
  const d = parseLocalDate(date);
  if (!d || d.getDay() !== 6) return false;
  const saturdayNumber = Math.ceil(d.getDate() / 7);
  return saturdayNumber === 2 || saturdayNumber === 4;
};

// Total calendar days between two date-only values, inclusive.
export const calculateDays = (startDate, endDate) => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (!start || !end) return '-';
  const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

// Working days (excludes Sundays and 2nd/4th Saturdays). Client-side estimate;
// the server validates against actual holidays.
export const calculateWorkingDays = (startDate, endDate) => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (!start || !end) return { workingDays: 0, offDays: [] };

  let workingDays = 0;
  const offDays = [];
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const saturdayNumber = Math.ceil(current.getDate() / 7);
    const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    if (dayOfWeek === 0) {
      offDays.push({ date: dateStr, reason: 'Sunday' });
    } else if (dayOfWeek === 6 && (saturdayNumber === 2 || saturdayNumber === 4)) {
      offDays.push({ date: dateStr, reason: `${saturdayNumber === 2 ? '2nd' : '4th'} Saturday` });
    } else {
      workingDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  return { workingDays, offDays };
};
